import { useEffect, useState } from "react";
import { Save, ShieldCheck } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Btn } from "../../components/Btn";
import { useToast } from "../../components/Toast";
import { getSettings, updateSettings } from "../../api/endpoints";
import { ApiError } from "../../api/types";

// Ключи системных настроек на бэке
const KEYS = {
  globalUrl: "catalog.global_url",
  pollInterval: "catalog.poll_interval_min",
  keepVersions: "catalog.keep_versions",
  auditRetention: "audit.retention_days",
  pwdPolicy: "security.password_policy", // "weak" | "default" | "strict"
  mfa: "security.mfa", // "true" | "false"
  tlsMin: "security.tls_min", // "1.2" | "1.3"
} as const;

const DEFAULTS = {
  globalUrl: "global.market.corp:8443",
  pollInterval: "10",
  keepVersions: "5",
  auditRetention: "365",
  pwdPolicy: "default" as "weak" | "default" | "strict",
  mfa: true,
  tlsMin: "1.3" as "1.2" | "1.3",
};

export default function AdminSettingsPage() {
  const push = useToast();
  const qc = useQueryClient();

  const settingsQ = useQuery({
    queryKey: ["settings"],
    queryFn: getSettings,
  });

  const [globalUrl, setGlobalUrl] = useState(DEFAULTS.globalUrl);
  const [pollInterval, setPollInterval] = useState(DEFAULTS.pollInterval);
  const [keepVersions, setKeepVersions] = useState(DEFAULTS.keepVersions);
  const [auditRetention, setAuditRetention] = useState(DEFAULTS.auditRetention);
  const [pwdPolicy, setPwdPolicy] = useState<"weak" | "default" | "strict">(DEFAULTS.pwdPolicy);
  const [mfa, setMfa] = useState<boolean>(DEFAULTS.mfa);
  const [tlsMin, setTlsMin] = useState<"1.2" | "1.3">(DEFAULTS.tlsMin);

  // Подтягиваем значения, как только settings прилетят
  useEffect(() => {
    const items = settingsQ.data?.items;
    if (!items) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- синхронизация формы с данными запроса
    if (items[KEYS.globalUrl]) setGlobalUrl(items[KEYS.globalUrl]);
    if (items[KEYS.pollInterval]) setPollInterval(items[KEYS.pollInterval]);
    if (items[KEYS.keepVersions]) setKeepVersions(items[KEYS.keepVersions]);
    if (items[KEYS.auditRetention]) setAuditRetention(items[KEYS.auditRetention]);
    const pp = items[KEYS.pwdPolicy];
    if (pp === "weak" || pp === "default" || pp === "strict") setPwdPolicy(pp);
    if (items[KEYS.mfa] !== undefined) setMfa(items[KEYS.mfa] === "true");
    const tls = items[KEYS.tlsMin];
    if (tls === "1.2" || tls === "1.3") setTlsMin(tls);
  }, [settingsQ.data]);

  const saveMut = useMutation({
    mutationFn: () =>
      updateSettings({
        items: {
          [KEYS.globalUrl]: globalUrl,
          [KEYS.pollInterval]: pollInterval,
          [KEYS.keepVersions]: keepVersions,
          [KEYS.auditRetention]: auditRetention,
          [KEYS.pwdPolicy]: pwdPolicy,
          [KEYS.mfa]: mfa ? "true" : "false",
          [KEYS.tlsMin]: tlsMin,
        },
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["settings"] });
      push({ kind: "success", text: "Системные настройки сохранены" });
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? (e.message || "Не удалось сохранить") : "Не удалось сохранить";
      push({ kind: "warn", text: msg });
    },
  });

  const loading = settingsQ.isLoading;

  return (
    <>
      <header className="flex flex-col gap-3 pb-5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-[20px] font-semibold leading-tight tracking-[-0.01em] text-[var(--color-fg)] sm:text-[22px]">
            Настройки системы
          </h1>
          <div className="mt-1 text-[12.5px] text-[var(--color-fg-muted)]">
            Глобальные параметры синхронизации, безопасности и хранения
          </div>
        </div>
        <Btn icon={Save} variant="primary" onClick={() => saveMut.mutate()}>
          {saveMut.isPending ? "Сохранение…" : "Сохранить"}
        </Btn>
      </header>

      <div className={"space-y-3 " + (loading ? "opacity-70" : "")}>
        <Section
          title="Каталог и синхронизация"
          hint="Параметры обмена между Глобальным сервером и локальными узлами."
        >
          <Field label="Адрес Глобального сервера" hint="Доступен Локальным серверам по mTLS.">
            <input
              value={globalUrl}
              onChange={(e) => setGlobalUrl(e.target.value)}
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 font-mono text-[13px] outline-none focus:border-[var(--color-fg-muted)]"
            />
          </Field>
          <Field label="Интервал опроса локалом, мин" hint="Через какой период локал сам идёт за обновлениями.">
            <input
              value={pollInterval}
              onChange={(e) => setPollInterval(e.target.value)}
              type="number"
              className="w-32 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 font-mono text-[13px] outline-none focus:border-[var(--color-fg-muted)]"
            />
          </Field>
          <Field label="Хранить N версий приложения" hint="Старые версии остаются доступны для отката.">
            <input
              value={keepVersions}
              onChange={(e) => setKeepVersions(e.target.value)}
              type="number"
              className="w-32 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 font-mono text-[13px] outline-none focus:border-[var(--color-fg-muted)]"
            />
          </Field>
        </Section>

        <Section
          title="Безопасность"
          hint="Политики паролей, аутентификации и шифрования каналов."
        >
          <Field label="Политика паролей" hint="Минимальная сложность пароля пользователя.">
            <div className="flex gap-2">
              {(["weak", "default", "strict"] as const).map((p) => (
                <button
                  key={p}
                  onClick={() => setPwdPolicy(p)}
                  className={
                    "rounded-lg border px-3 py-1.5 text-[12.5px] font-medium transition-colors " +
                    (pwdPolicy === p
                      ? "border-[var(--color-brand)] bg-[var(--color-brand)] text-white"
                      : "border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-fg)] hover:bg-[var(--color-surface-2)]")
                  }
                >
                  {p === "weak" ? "Слабая" : p === "default" ? "Стандарт" : "Строгая"}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Двухфакторная аутентификация" hint="Требовать MFA для всех администраторов.">
            <Toggle checked={mfa} onChange={setMfa} />
          </Field>

          <Field label="Минимальная версия TLS" hint="Используется во всех соединениях контура.">
            <div className="flex gap-2">
              {(["1.2", "1.3"] as const).map((v) => (
                <button
                  key={v}
                  onClick={() => setTlsMin(v)}
                  className={
                    "rounded-lg border px-3 py-1.5 font-mono text-[12.5px] transition-colors " +
                    (tlsMin === v
                      ? "border-[var(--color-brand)] bg-[var(--color-brand)] text-white"
                      : "border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-fg)] hover:bg-[var(--color-surface-2)]")
                  }
                >
                  TLS {v}
                </button>
              ))}
            </div>
          </Field>
        </Section>

        <Section title="Аудит и журналы" hint="Сколько хранить события в Глобальном журнале.">
          <Field label="Срок хранения событий, дней">
            <input
              value={auditRetention}
              onChange={(e) => setAuditRetention(e.target.value)}
              type="number"
              className="w-32 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 font-mono text-[13px] outline-none focus:border-[var(--color-fg-muted)]"
            />
          </Field>
          <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3 text-[12px] text-[var(--color-fg-muted)]">
            <div className="flex items-center gap-2 text-[var(--color-fg)]">
              <ShieldCheck size={14} className="text-[var(--color-success)]" />
              <span className="font-medium">Журналы передаются по цепочке устройство → локал → глобал.</span>
            </div>
            <div className="mt-1">Подписаны узлами, изменения задним числом обнаруживаются процедурой проверки целостности.</div>
          </div>
        </Section>
      </div>
    </>
  );
}

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <h2 className="text-[14px] font-semibold text-[var(--color-fg)]">{title}</h2>
      {hint && <p className="mt-0.5 text-[11.5px] text-[var(--color-fg-muted)]">{hint}</p>}
      <div className="mt-4 space-y-3.5">{children}</div>
    </section>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-[260px_1fr]">
      <div>
        <div className="text-[12.5px] font-medium text-[var(--color-fg)]">{label}</div>
        {hint && <div className="text-[11px] text-[var(--color-fg-muted)]">{hint}</div>}
      </div>
      <div>{children}</div>
    </div>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={
        "relative inline-flex h-6 w-11 items-center rounded-full transition-colors " +
        (checked ? "bg-[var(--color-brand)]" : "bg-[var(--color-surface-3)]")
      }
    >
      <span
        className={
          "inline-block h-5 w-5 rounded-full bg-[var(--color-surface)] shadow transition-transform " +
          (checked ? "translate-x-5" : "translate-x-0.5")
        }
      />
    </button>
  );
}
